import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { apiErrorMessage, apiFieldErrors } from "../services/api";
import { createUser, updateUser } from "../services/userService";
import Toggle from "./Toggle";

const EMPTY = {
  name: "",
  email: "",
  password: "",
  passwordConfirmation: "",
  roleId: "",
  isActive: true,
  branchIds: [],
};

/**
 * The branches a user is already assigned to: the ones they run plus the ones
 * their login may reach. Both lists are kept in step from now on, but a manager
 * assigned before that was true holds `manager_id` with no pivot row — seeding
 * from either list alone would show their own branches unticked and quietly
 * drop them on the next save.
 */
const assignedBranchIds = (user) => [
  ...new Set([
    ...(user.managed_branches ?? []).map((branch) => branch.id),
    ...(user.branches ?? []).map((branch) => branch.id),
  ]),
];

/**
 * Create or edit one staff account. `user` null means create.
 *
 * The branch multi-select is the user's branch assignment. What it means
 * depends on the role — see the rule below the field list.
 */
export default function StaffModal({ user, branches, roles = [], open, onClose, onSaved }) {
  const { t } = useTranslation();
  const [values, setValues] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [banner, setBanner] = useState("");
  const [saving, setSaving] = useState(false);

  const editing = Boolean(user);
  // The owner's role and active flag are fixed: a business must keep its owner.
  const isOwner = Boolean(user?.is_owner);

  useEffect(() => {
    if (!open) return;

    setValues(
      user
        ? {
            name: user.name ?? "",
            email: user.email ?? "",
            password: "",
            passwordConfirmation: "",
            roleId: user.role_id ?? "",
            isActive: Boolean(user.is_active),
            branchIds: assignedBranchIds(user),
          }
        : EMPTY
    );
    setErrors({});
    setBanner("");
  }, [open, user]);

  useEffect(() => {
    if (!open) return undefined;

    const onKey = (e) => {
      if (e.key === "Escape" && !saving) onClose();
    };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";

    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [open, onClose, saving]);

  if (!open) return null;

  const set = (field) => (e) => setValues((v) => ({ ...v, [field]: e.target.value }));
  const fieldError = (name) => errors[name]?.[0];

  /**
   * OUP-41 — every role but the owner is assigned to branches, and the labels
   * say what the assignment means:
   *
   * - Branch Manager: the branches they *run*. A branch has one manager, so
   *   ticking one takes it from whoever ran it before.
   * - Receptionist, staff, or a role the business invented: the branches they
   *   *work at*. It grants their login access to those branches and leaves
   *   every branch's manager alone.
   * - Owner: hidden. They already reach every branch of the business, and
   *   sending `branch_ids` for them would strip manager_id off the branches
   *   they run.
   *
   * "Owner" means the role this form is about to save, not only the owner
   * being edited. `isOwner` answers the second and is false for every create,
   * so on the create screen the section stayed visible — and required — under
   * the owner role, refusing a save the API would have taken. The API reads the
   * role_id being sent (`BusinessUserRequest::effectiveRoleIsOwner()`), so the
   * form reads the same thing.
   *
   * OUP-19 rendered this for the Branch Manager alone, which is what left a
   * receptionist with no way to be pointed at a branch at all. Its other half
   * still holds: the section is never shown under a heading that misdescribes
   * what it does.
   *
   * Hidden means omitted, not emptied: the controller keys on
   * array_key_exists('branch_ids'), so an absent key leaves an existing
   * assignment alone while an empty array clears it.
   */
  const selectedRole = roles.find((role) => String(role.id) === String(values.roleId));
  const roleSlug = selectedRole?.slug ?? null;
  const managesBranches = roleSlug === "manager";
  // The owner being edited keeps their role; anybody else is the owner only if
  // the role picked in this form is.
  const savingAsOwner = isOwner || roleSlug === "owner";
  const showBranches = !savingAsOwner;
  // Required of every role but the owner's — a login assigned no branch reaches
  // nothing. A business with no branches yet is the one exemption, or its first
  // staff account could never be saved; the API draws the same line.
  const branchesRequired = showBranches && branches.length > 0;

  const toggleBranch = (branchId) =>
    setValues((v) => ({
      ...v,
      branchIds: v.branchIds.includes(branchId)
        ? v.branchIds.filter((id) => id !== branchId)
        : [...v.branchIds, branchId],
    }));

  /** Mirrors the API's rules so an obvious slip costs no round trip. */
  const validate = () => {
    const found = {};

    if (values.name.trim().length < 2) found.name = [t("staff.form.errors.name")];
    if (!/^\S+@\S+\.\S+$/.test(values.email.trim())) found.email = [t("staff.form.errors.email")];
    // The owner's role is fixed and the select is disabled, so it is exempt.
    if (!isOwner && !values.roleId) found.role_id = [t("staff.form.errors.role")];

    if (branchesRequired && values.branchIds.length === 0) {
      found.branch_ids = [t("staff.form.errors.branches")];
    }

    // A password is required when creating, optional when editing.
    if (!editing || values.password) {
      if (values.password.length < 8) found.password = [t("staff.form.errors.password")];
      else if (values.password !== values.passwordConfirmation) {
        found.password = [t("staff.form.errors.passwordConfirm")];
      }
    }

    return found;
  };

  const submit = async (e) => {
    e.preventDefault();
    setBanner("");

    const clientErrors = validate();
    if (Object.keys(clientErrors).length > 0) {
      setErrors(clientErrors);
      return;
    }

    setErrors({});
    setSaving(true);

    try {
      // Not rendered means not submitted — the owner case, whether that is the
      // owner being edited or the owner role picked on the create screen.
      const payload = showBranches ? values : { ...values, branchIds: undefined };

      const saved = editing ? await updateUser(user.id, payload) : await createUser(payload);
      onSaved(saved, editing);
      onClose();
    } catch (err) {
      setErrors(apiFieldErrors(err));
      setBanner(apiErrorMessage(err, t("staff.form.saveFailed")));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget && !saving) onClose();
      }}
    >
      <div role="dialog" aria-modal="true" className="w-full max-w-lg rounded-xl bg-surface shadow-xl">
        <form onSubmit={submit} noValidate>
          <div className="flex items-center justify-between border-b border-line px-5 py-3">
            <h2 className="text-sm font-semibold">
              {editing ? t("staff.form.editTitle") : t("staff.form.createTitle")}
            </h2>
            <button
              type="button"
              onClick={onClose}
              disabled={saving}
              aria-label={t("common.close")}
              className="rounded p-1 text-ink-muted hover:bg-raised hover:text-ink-2"
            >
              ✕
            </button>
          </div>

          <div className="max-h-[70vh] space-y-4 overflow-y-auto px-5 py-4">
            {banner && (
              <div className="rounded-lg border border-danger-line bg-danger-wash px-3 py-2 text-sm text-danger-text">
                {banner}
              </div>
            )}

            <fieldset disabled={saving} className="space-y-4">
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <label className="field-label" htmlFor="staff-name">
                    {t("staff.form.name")} <span className="text-danger-text">*</span>
                  </label>
                  <input
                    id="staff-name"
                    className="field-input"
                    value={values.name}
                    onChange={set("name")}
                  />
                  {fieldError("name") && <p className="field-error">{fieldError("name")}</p>}
                </div>

                <div>
                  <label className="field-label" htmlFor="staff-email">
                    {t("staff.form.email")} <span className="text-danger-text">*</span>
                  </label>
                  <input
                    id="staff-email"
                    type="email"
                    dir="ltr"
                    className="field-input"
                    value={values.email}
                    onChange={set("email")}
                  />
                  <p className="mt-1 text-xs text-ink-2">{t("staff.form.emailHint")}</p>
                  {fieldError("email") && <p className="field-error">{fieldError("email")}</p>}
                </div>

                <div>
                  <label className="field-label" htmlFor="staff-password">
                    {t("staff.form.password")}
                    {!editing && <span className="text-danger-text"> *</span>}
                  </label>
                  <input
                    id="staff-password"
                    type="password"
                    dir="ltr"
                    autoComplete="new-password"
                    className="field-input"
                    value={values.password}
                    onChange={set("password")}
                  />
                  <p className="mt-1 text-xs text-ink-2">
                    {editing ? t("staff.form.passwordEditHint") : t("staff.form.passwordHint")}
                  </p>
                  {fieldError("password") && <p className="field-error">{fieldError("password")}</p>}
                </div>

                <div>
                  <label className="field-label" htmlFor="staff-password-confirm">
                    {t("staff.form.passwordConfirmation")}
                    {!editing && <span className="text-danger-text"> *</span>}
                  </label>
                  <input
                    id="staff-password-confirm"
                    type="password"
                    dir="ltr"
                    autoComplete="new-password"
                    className="field-input"
                    value={values.passwordConfirmation}
                    onChange={set("passwordConfirmation")}
                  />
                </div>
              </div>

              <div>
                <label className="field-label" htmlFor="staff-role">
                  {t("staff.form.role")} <span className="text-danger-text">*</span>
                </label>
                <select
                  id="staff-role"
                  className="field-input"
                  disabled={isOwner}
                  value={values.roleId}
                  onChange={set("roleId")}
                >
                  {/* The role decides whether the branch section below is shown,
                      so an unchosen role must read as unchosen rather than
                      silently landing on whichever role happens to sort first. */}
                  <option value="">{t("staff.form.rolePlaceholder")}</option>

                  {/* The business's own roles: the four it starts with, plus
                      any it has added. System ones are labelled per language. */}
                  {roles.map((role) => (
                    <option key={role.id} value={role.id}>
                      {role.slug ? t(`roles.system.${role.slug}`, role.name) : role.name}
                    </option>
                  ))}
                </select>
                {isOwner && <p className="mt-1 text-xs text-ink-2">{t("staff.form.ownerLocked")}</p>}
                {fieldError("role_id") && <p className="field-error">{fieldError("role_id")}</p>}
              </div>

              {showBranches && (
              <div>
                <span className="field-label">
                  {managesBranches ? t("staff.form.branches") : t("staff.form.worksAt")}
                  {branchesRequired && <span className="text-danger-text"> *</span>}
                </span>
                <p className="mb-2 text-xs text-ink-2">
                  {managesBranches ? t("staff.form.branchesHint") : t("staff.form.worksAtHint")}
                </p>

                {branches.length === 0 ? (
                  <p className="text-sm text-ink-2">{t("staff.form.noBranches")}</p>
                ) : (
                  <div className="max-h-40 space-y-1 overflow-y-auto rounded-lg border border-line p-2">
                    {branches.map((branch) => {
                      // Warn when ticking this takes the branch off someone
                      // else. Only a manager takes one: for every other role
                      // the branch keeps the manager it has.
                      const takenFrom =
                        managesBranches && branch.manager && branch.manager.id !== user?.id
                          ? branch.manager.name
                          : null;

                      return (
                        <label
                          key={branch.id}
                          className="flex cursor-pointer items-start gap-2 rounded px-1 py-1 text-sm hover:bg-raised"
                        >
                          <input
                            type="checkbox"
                            className="mt-1 h-4 w-4 rounded border-line-strong"
                            checked={values.branchIds.includes(branch.id)}
                            onChange={() => toggleBranch(branch.id)}
                          />
                          <span className="min-w-0">
                            <span className="block">{branch.name}</span>
                            {takenFrom && values.branchIds.includes(branch.id) && (
                              <span className="block text-xs text-warning-text">
                                {t("staff.form.takenFrom", { name: takenFrom })}
                              </span>
                            )}
                          </span>
                        </label>
                      );
                    })}
                  </div>
                )}
                {(fieldError("branch_ids") || errors["branch_ids.0"]?.[0]) && (
                  <p className="field-error">
                    {fieldError("branch_ids") || errors["branch_ids.0"][0]}
                  </p>
                )}
              </div>
              )}

              <div className="rounded-lg border border-line p-3">
                <Toggle
                  id="staff-active"
                  checked={values.isActive}
                  disabled={isOwner}
                  onChange={(checked) => setValues((v) => ({ ...v, isActive: checked }))}
                  label={t("staff.form.active")}
                  hint={t("staff.form.activeHint")}
                  error={fieldError("is_active")}
                />
              </div>
            </fieldset>
          </div>

          <div className="flex justify-end gap-2 border-t border-line px-5 py-3">
            <button type="button" onClick={onClose} disabled={saving} className="btn-secondary">
              {t("common.cancel")}
            </button>
            <button type="submit" disabled={saving} className="btn-primary">
              {saving ? t("staff.form.saving") : t("common.save")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
